<template>
  <div class="absolute inset-0 flex flex-col gap-4 p-6">
    <div class="flex flex-row space-y-1.5 text-center justify-between">
      <div class="flex flex-col items-start">
        <h2
          id="radix-_r_2j_"
          class="text-lg font-semibold leading-none tracking-tight"
        >
          Schedules for Product: {{ product_code }}
        </h2>
        <p id="radix-_r_2k_" class="text-sm text-muted-foreground">
          update and manage price change schedules for this product.
        </p>
      </div>
      <i class="el-icon-close" @click="$emit('cancel')" />
    </div>
    <div class="flex items-center justify-between">
      <!--tabbar-->
      <span />
      <!--search panel-->
      <div class="flex items-center gap-2">
        <div class="relative">
          <el-input
            placeholder="Search..."
            prefix-icon="el-icon-search"
            class="w-[250px]"
            clearable
            v-model="searchWords"
          />
        </div>
        <el-button @click="handleSchedule()" type="primary" icon="el-icon-plus">
          Add Schedule
        </el-button>
        <el-popover
          placement="bottom"
          trigger="click"
          width="320"
          v-model="filterPanelVisible"
        >
          <button
            slot="reference"
            class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border-input bg-background hover:bg-accent hover:text-accent-foreground border h-10 w-10"
            type="button"
          >
            <i class="icon-funnel h-4 w-4" />
          </button>
          <ETagFilterPanel @apply="handleApply" />
        </el-popover>
      </div>
    </div>
    <div class="flex-1 relative">
      <el-table
        :data="isLoading ? [] : tableCtrl?.state?.tableData"
        style="width: 100%"
        v-loading="isLoading"
        element-loading-background="rgba(24, 33, 48, 0.8)"
        empty-text="No schedules found."
        height="auto"
        class="absolute inset-0"
        border
      >
        <el-table-column prop="name" label="Schedule Name" min-width="120">
          <template #default="{ row }">
            {{ row.name || "--" }}
          </template>
        </el-table-column>
        <el-table-column label="Product Name" min-width="120">
          <template #default="{ row }">
            {{ row.items.map((i) => i.product_code).join(", ") || "--" }}
          </template>
        </el-table-column>
        <el-table-column label="Price Change" min-width="160">
          <template #default="{ row }">
            <div class="flex items-center gap-2">
              <span class="line-through text-muted-foreground">
                ${{
                  row.items.map((i) => i.start_action.product_price).join(", ")
                }}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-arrow-up-down h-3 w-3 text-muted-foreground"
              >
                <path d="m21 16-4 4-4-4"></path>
                <path d="M17 20V4"></path>
                <path d="m3 8 4-4 4 4"></path>
                <path d="M7 4v16"></path>
              </svg>
              <span class="font-medium">
                ${{
                  row.items.map((i) => i.end_action.product_price).join(", ") ||
                  "--"
                }}
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Start Date" min-width="100">
          <template #default="{ row }">
            {{ row.start_time ? row.start_time.split("T")[0] : "--" }}
          </template>
        </el-table-column>
        <el-table-column label="End Date" min-width="100">
          <template #default="{ row }">
            {{ row.end_time ? row.end_time.split("T")[0] : "Indefinite" }}
          </template>
        </el-table-column>
        <el-table-column label="Actions" min-width="120" align="right">
          <template #default="{ row }">
            <div class="flex justify-end gap-4">
              <i
                class="fa fa-edit text-xl cursor-pointer"
                style="color: #fcb900"
                @click="handleSchedule(row)"
              />
              <i
                class="fa fa-trash text-xl text-red-500 cursor-pointer"
                @click="handleDeleteSchedule(row.id)"
              />
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <div class="items-center flex justify-between">
      <div class="flex flex-row">
        <el-button icon="fa fa-download"> Export Schedule </el-button>
      </div>
      <el-pagination
        @size-change="(val) => tableCtrl.setPageSize(val)"
        @current-change="(val) => tableCtrl.setCurrentPage(val)"
        :current-page="tableCtrl?.state?.pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="tableCtrl?.state?.pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="tableCtrl?.state?.pagination.total"
      />
    </div>
  </div>
</template>

<script>
import { TableController } from "@/controllers/TableController";
import { listPriceTasks, deletePriceTasks } from "@/api";
import ETagFilterPanel from "./ETagFilterPanel.vue";

export default {
  name: "SchduleListByProudctCode",
  props: {
    product_code: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      tableCtrl: null,
      filterPanelVisible: false,
      searchWords: "",
    };
  },
  mounted() {
    this.initTableController();
    window.vm = this.tableCtrl;
  },
  watch: {
    searchWords() {
      if (!this.tableCtrl) return;
      this.tableCtrl.setQueryParameters({ name: this.searchText });
    },
  },
  computed: {
    isLoading() {
      return this.tableCtrl?.state.loading ?? false;
    },
  },
  methods: {
    initTableController() {
      const me = this;
      const product_code = me.product_code;
      me.tableCtrl = new TableController({
        loadMethod: listPriceTasks,
        pageSize: 10,
        isClientPaged: true,
        keyField: "id",
        listField: "taskList",
        queryParameters: {
          product_code: product_code,
        },
      });
    },
    async handleDeleteSchedule(id) {
      await this.$confirm(
        "Are you sure you want to delete this schedule?",
        "Confirm Deletion",
        {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      );
      try {
        await deletePriceTasks([id]);
      } catch (e) {
        this.$message.error("Failed to delete schedule: " + e.message);
        return;
      }
      this.$message.success("Schedule deleted successfully");
      this.tableCtrl.loadTableData();
    },
    async handleSchedule(item) {
      const result = await this.$loaderWindow({
        title: `Create Schedule`,
        component: () => import("./EditTaskSchedule.vue"),
        width: "800px",
        showHeader: false,
        padding: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: {
          value: item,
        },
      });
      this.$message.success("Schedule created successfully");
      if (result) this.tableCtrl.loadTableData();
    },
    handleApply() {
      this.filterPanelVisible = false;
      this.tableCtrl.loadTableData();
    },
  },
  components: {
    ETagFilterPanel,
  },
};
</script>

<style lang="scss" scoped>
.schedules-board {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
}

.schedule-item {
  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }

  p {
    margin: 4px 0 8px;
    color: #4b5563;
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 13px;
    color: #6b7280;
  }
}

.empty-state {
  margin-top: 24px;
  text-align: center;
  color: #6b7280;
}
</style>
